import React, { createContext, FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom';

import { apiRequest } from './api';
import { formatModelProgress, formatTerminationMessage } from './modelProgress';
import { ReplicaLogs } from './ReplicaLogs';
import { buildStopRequest } from './serviceActions';
import { statusTone } from './status';

import { APIKey, APIKeyCreated, Event, Fleet, GPU, Instance, Registry, ReplicaEntry, ServiceResponse } from './types';

import logo from './assets/images/logo.svg';

const TOKEN_KEY = 'vstack-root-token';
type Principal = {
    auth_mode: 'root' | 'memhub';
    subject: string;
    name: string;
    role: 'viewer' | 'admin';
    application_url?: string;
};
const AuthContext = createContext<{ token: string; principal: Principal } | null>(null);
function useAuth() {
    const auth = useContext(AuthContext);
    if (!auth) throw new Error('Not authenticated');
    return auth;
}
function useAdmin() {
    return useAuth().principal.role === 'admin';
}

function useLoad<T>(token: string, path: string, body: unknown = {}, enabled = true) {
    const [data, setData] = useState<T>();
    const [error, setError] = useState('');
    const reload = useCallback(async () => {
        if (!enabled) return;
        setError('');
        try {
            setData(await apiRequest<T>(token, path, body));
        } catch (reason) {
            setError(String(reason));
        }
    }, [token, path, JSON.stringify(body), enabled]);
    useEffect(() => void reload(), [reload]);
    return { data, error, reload };
}

function ErrorText({ error }: { error: string }) {
    return error ? <p className="error">{error}</p> : null;
}

function Status({ value }: { value: string }) {
    return <span className={`status status-${statusTone(value)}`}>{value}</span>;
}

function Login({ onLogin }: { onLogin: (token: string) => void }) {
    const [memhub, setMemhub] = useState(false);
    useEffect(() => {
        fetch('/api/auth/options')
            .then((response) => response.json())
            .then((options) => setMemhub(options.memhub))
            .catch(() => setMemhub(false));
    }, []);
    const [token, setToken] = useState('');
    const [error, setError] = useState('');
    const submit = async (event: FormEvent) => {
        event.preventDefault();
        try {
            await apiRequest<void>(token, 'auth/check');
            localStorage.setItem(TOKEN_KEY, token);
            onLogin(token);
        } catch (reason) {
            setError(String(reason));
        }
    };
    return (
        <form className="login" onSubmit={submit}>
            <img src={logo} alt="vstack" width="150" />
            <h1>Internal GPU Services</h1>
            {memhub && (
                <p>
                    <a className="primary" href="/api/auth/memhub/start">
                        Sign in with MemHub
                    </a>
                </p>
            )}
            <p className="muted">Root token access</p>
            <input type="password" value={token} onChange={(event) => setToken(event.target.value)} required autoFocus />
            <div className="toolbar">
                <button className="primary" type="submit">
                    Sign in
                </button>
            </div>
            <ErrorText error={error} />
        </form>
    );
}

function Layout({ logout, refresh }: { logout: () => void; refresh: () => void }) {
    const { principal } = useAuth();
    const admin = useAdmin();
    const links = [
        ['/services', 'GPU Services'],
        ['/fleets', 'SSH Fleets'],
        ['/hosts', 'GPU Hosts'],
        ['/api-keys', 'API Keys'],
        ['/registries', 'Registries'],
        ['/events', 'Events'],
    ].filter(([href]) => admin || href !== '/api-keys');
    return (
        <div className="shell">
            <aside>
                <img src={logo} alt="vstack" />
                <nav>
                    {links.map(([href, label]) => (
                        <NavLink key={href} to={href}>
                            {label}
                        </NavLink>
                    ))}
                </nav>
                <p>
                    {principal.name} · {principal.role}
                </p>
                {principal.application_url && (
                    <a href={principal.application_url} target="_blank" rel="noreferrer">
                        Apply for admin in MemHub
                    </a>
                )}
                <div className="toolbar">
                    <button onClick={refresh}>Refresh permissions</button>
                    <button onClick={logout}>Sign out</button>
                </div>
            </aside>
            <main>
                <Routes>
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/services/:id" element={<ServiceDetailsPage />} />
                    <Route path="/fleets" element={<FleetsPage />} />
                    <Route path="/hosts" element={<HostsPage />} />
                    <Route path="/api-keys" element={admin ? <APIKeysPage /> : <Navigate replace to="/services" />} />
                    <Route path="/registries" element={<RegistriesPage />} />
                    <Route path="/events" element={<EventsPage />} />
                    <Route path="*" element={<Navigate replace to="/services" />} />
                </Routes>
            </main>
        </div>
    );
}

function useToken() {
    return useAuth().token;
}

const defaultService = JSON.stringify(
    {
        name: 'inference',
        spec: {
            replicas: 1,
            replica_template: {
                image: 'registry.example/inference:latest',
                resources: { gpu: 1 },
                env: {},
                port: 8000,
            },
            proxy: {},
        },
    },
    null,
    2,
);

function ServicesPage() {
    const admin = useAdmin();
    const token = useToken();
    const { data = [], error, reload } = useLoad<ServiceResponse[]>(token, 'services/list');
    const [draft, setDraft] = useState('');
    const [actionError, setActionError] = useState('');
    const create = async () => {
        try {
            await apiRequest(token, 'services/create', JSON.parse(draft));
            setDraft('');
            await reload();
        } catch (reason) {
            setActionError(String(reason));
        }
    };
    return (
        <>
            <h1>GPU Services</h1>
            <div className="toolbar">
                {admin && (
                    <button className="primary" onClick={() => setDraft(defaultService)}>
                        Create service
                    </button>
                )}
                <button onClick={reload}>Refresh</button>
            </div>
            {draft && (
                <div className="card">
                    <h2>Create service</h2>
                    <p className="muted">Environment values are stored and passed literally.</p>
                    <p className="muted">
                        Optional replica_template.volumes: source is an operator-prepared host directory, target is the
                        container path. Paths are not checked during scheduling. Mode defaults to ro.
                    </p>
                    <pre>
                        {JSON.stringify(
                            { volumes: [{ source: '/mnt/models/comfyui', target: '/root/ComfyUI/models', mode: 'ro' }] },
                            null,
                            2,
                        )}
                    </pre>
                    <textarea value={draft} onChange={(event) => setDraft(event.target.value)} />
                    <div className="toolbar">
                        <button className="primary" onClick={create}>
                            Create
                        </button>
                        <button onClick={() => setDraft('')}>Cancel</button>
                    </div>
                    <ErrorText error={actionError} />
                </div>
            )}
            <ErrorText error={error} />
            <div className="card">
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Replicas</th>
                            <th>Gateway</th>
                            <th>OpenAI</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((service) => (
                            <tr key={service.id}>
                                <td>
                                    <NavLink to={`/services/${service.id}`}>{service.name}</NavLink>
                                </td>
                                <td>
                                    <Status value={service.status} />
                                </td>
                                <td>
                                    {service.ready_replica_count}/{service.desired_replica_count}
                                </td>
                                <td>{service.gateway?.http_url ?? 'pending'}</td>
                                <td>
                                    {service.gateway?.openai
                                        ? [service.gateway.openai.model_id, ...service.gateway.openai.aliases].join(', ')
                                        : '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function ServiceDetailsPage() {
    const admin = useAdmin();
    const token = useToken();
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const { data: service, error, reload } = useLoad<ServiceResponse>(token, 'services/get', { id });
    const { data: revisions = [], reload: reloadRevisions } = useLoad<
        Array<{ revision_number: number; source: string; spec: unknown }>
    >(token, 'services/revisions/list', { id }, admin);
    const { data: replicas = [] } = useLoad<ReplicaEntry[]>(token, 'replicas/list', {
        service_id: id,
    });
    const [draft, setDraft] = useState('');
    const [scale, setScale] = useState('1');
    const [actionError, setActionError] = useState('');
    const [logsReplica, setLogsReplica] = useState<ReplicaEntry>();
    const run = async (path: string, body: unknown) => {
        try {
            await apiRequest(token, path, body);
            setActionError('');
            await Promise.all([reload(), reloadRevisions()]);
        } catch (reason) {
            setActionError(String(reason));
        }
    };
    if (!service)
        return (
            <>
                <h1>Service</h1>
                <ErrorText error={error} />
            </>
        );
    const managedService = 'spec' in service ? service : undefined;
    const stop = managedService ? buildStopRequest(managedService) : undefined;
    return (
        <>
            <div className="toolbar">
                <button onClick={() => navigate('/services')}>Back</button>
                <h1>{service.name}</h1>
            </div>
            <div className="grid">
                <div className="card">
                    <b>Status</b>
                    <p>
                        <Status value={service.status} />
                    </p>
                </div>
                <div className="card">
                    <b>Replicas</b>
                    <p>
                        {service.ready_replica_count} ready / {service.desired_replica_count} desired
                    </p>
                </div>
                <div className="card">
                    <b>Gateway</b>
                    <p>{service.gateway?.http_url ?? 'pending'}</p>
                    <p>{service.gateway?.grpc_namespace ?? 'gRPC disabled'}</p>
                    <p>
                        {service.gateway?.openai
                            ? `${service.gateway.openai.base_url} — ${[
                                  service.gateway.openai.model_id,
                                  ...service.gateway.openai.aliases,
                              ].join(', ')}`
                            : 'OpenAI disabled'}
                    </p>
                </div>
                <div className="card">
                    <b>Request admission</b>
                    <p>
                        {service.request_admission
                            ? `${service.request_admission.active_requests} active / ${
                                  service.request_admission.waiting_requests
                              } waiting (queue ${service.request_admission.queue_capacity}, replica ${
                                  service.request_admission.replica_concurrency ?? 'unlimited'
                              })`
                            : 'Disabled'}
                    </p>
                </div>
                <div className="card">
                    <b>Recent failure</b>
                    <p>
                        {service.recent_failure
                            ? `${service.recent_failure.reason} at ${new Date(service.recent_failure.occurred_at).toLocaleString()}`
                            : 'None'}
                    </p>
                </div>
            </div>
            {admin && managedService && stop && (
                <div className="toolbar">
                    <button className="danger" onClick={() => confirm('Stop this service?') && run(stop.path, stop.body)}>
                        Stop
                    </button>
                    {managedService.spec.replicas != null && (
                        <>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={scale}
                                onChange={(event) => setScale(event.target.value)}
                            />
                            <button
                                onClick={() =>
                                    run('services/scale', {
                                        id,
                                        replicas: Number(scale),
                                        expected_revision_number: service.target_revision_number,
                                    })
                                }
                            >
                                Scale
                            </button>
                        </>
                    )}
                    <button onClick={() => setDraft(JSON.stringify(managedService.spec, null, 2))}>Update</button>
                    <button
                        className="danger"
                        onClick={async () => {
                            if (!confirm('Delete this service?')) return;
                            await apiRequest(token, 'services/delete', {
                                id,
                                expected_revision_number: service.target_revision_number,
                            });
                            navigate('/services');
                        }}
                    >
                        Delete
                    </button>
                </div>
            )}
            {draft && (
                <div className="card">
                    <h2>Update revision</h2>
                    <textarea value={draft} onChange={(event) => setDraft(event.target.value)} />
                    <div className="toolbar">
                        <button
                            className="primary"
                            onClick={() =>
                                run('services/update', {
                                    id,
                                    spec: JSON.parse(draft),
                                    expected_revision_number: service.target_revision_number,
                                })
                            }
                        >
                            Update
                        </button>
                        <button onClick={() => setDraft('')}>Cancel</button>
                    </div>
                </div>
            )}
            <ErrorText error={actionError || error} />
            {admin && logsReplica && (
                <ReplicaLogs
                    key={logsReplica.id}
                    token={token}
                    replica={logsReplica}
                    onClose={() => setLogsReplica(undefined)}
                />
            )}
            {admin && (
                <div className="card">
                    <h2>Revisions</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>Revision</th>
                                <th>Source</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {revisions.map((revision) => (
                                <tr key={revision.revision_number}>
                                    <td>{revision.revision_number}</td>
                                    <td>{revision.source}</td>
                                    <td>
                                        {revision.revision_number !== service.target_revision_number && (
                                            <button
                                                onClick={() =>
                                                    run('services/rollback', {
                                                        id,
                                                        revision_number: revision.revision_number,
                                                        expected_revision_number: service.target_revision_number,
                                                    })
                                                }
                                            >
                                                Rollback
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="card">
                <h2>Replicas</h2>
                <table className="replicas">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Status</th>
                            <th>Ready</th>
                            <th>Host</th>
                            <th>GPU</th>
                            <th>Model</th>
                            <th>Message</th>
                            {admin && <th>Logs</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {replicas.map((replica) => (
                            <tr key={replica.id}>
                                <td title={replica.id}>{replica.id}</td>
                                <td>
                                    <Status value={replica.status} />
                                </td>
                                <td>{String(replica.ready)}</td>
                                <td>{replica.instance_name ?? '-'}</td>
                                <td>{replica.gpu_ids?.join(', ') ?? '-'}</td>
                                <td>{formatModelProgress(replica.model_prepare_progress)}</td>
                                <td>{formatTerminationMessage(replica.termination_message)}</td>
                                {admin && (
                                    <td>
                                        {replica.logs_url && <button onClick={() => setLogsReplica(replica)}>Live logs</button>}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

const defaultFleet = JSON.stringify(
    {
        type: 'fleet',
        name: 'gpu-hosts',
        ssh_config: { user: 'ubuntu', port: 22, hosts: ['gpu-01.internal'] },
        env: {},
        blocks: 1,
    },
    null,
    2,
);

function FleetsPage() {
    const admin = useAdmin();
    const token = useToken();
    const { data = [], error, reload } = useLoad<Fleet[]>(token, 'fleets/list');
    const [draft, setDraft] = useState('');
    const [actionError, setActionError] = useState('');
    const apply = async () => {
        try {
            const spec = JSON.parse(draft);
            const plan = await apiRequest<{ spec: Record<string, unknown>; current_resource: unknown }>(
                token,
                'fleets/get_plan',
                { spec },
            );
            await apiRequest(token, 'fleets/apply', {
                plan: { spec: plan.spec, current_resource: plan.current_resource },
                force: false,
            });
            setDraft('');
            await reload();
        } catch (reason) {
            setActionError(String(reason));
        }
    };
    return (
        <>
            <h1>SSH Fleets</h1>
            <p className="muted">The platform SSH public key must already be installed on each host and proxy jump.</p>
            <div className="toolbar">
                {admin && (
                    <button className="primary" onClick={() => setDraft(defaultFleet)}>
                        Add fleet
                    </button>
                )}
                <button onClick={reload}>Refresh</button>
            </div>
            {draft && (
                <div className="card">
                    <h2>Fleet connection configuration</h2>
                    <textarea value={draft} onChange={(event) => setDraft(event.target.value)} />
                    <div className="toolbar">
                        <button className="primary" onClick={apply}>
                            Plan and apply
                        </button>
                        <button onClick={() => setDraft('')}>Cancel</button>
                    </div>
                    <ErrorText error={actionError} />
                </div>
            )}
            <ErrorText error={error} />
            <div className="card">
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Hosts</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((fleet) => (
                            <tr key={fleet.id}>
                                <td>{fleet.name}</td>
                                <td>
                                    <Status value={fleet.status} />
                                </td>
                                <td>{fleet.instances.map((item) => item.hostname ?? item.name).join(', ')}</td>
                                <td>
                                    {admin && (
                                        <button
                                            className="danger"
                                            onClick={async () => {
                                                if (confirm(`Delete ${fleet.name}?`)) {
                                                    await apiRequest(token, 'fleets/delete', { names: [fleet.name] });
                                                    await reload();
                                                }
                                            }}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function HostsPage() {
    const admin = useAdmin();
    const token = useToken();
    const { data: instances = [], error, reload } = useLoad<Instance[]>(token, 'instances/list');
    const { data: gpus = { gpus: [] } } = useLoad<{ gpus: GPU[] }>(token, 'gpus/list');
    const grouped = new Map<string, GPU[]>();
    for (const gpu of gpus.gpus ?? []) grouped.set(gpu.instance_id, [...(grouped.get(gpu.instance_id) ?? []), gpu]);
    return (
        <>
            <h1>GPU Hosts</h1>
            <div className="toolbar">
                <button onClick={reload}>Refresh</button>
            </div>
            <ErrorText error={error} />
            <div className="card">
                <table>
                    <thead>
                        <tr>
                            <th>Host</th>
                            <th>Fleet</th>
                            <th>Status</th>
                            <th>Health</th>
                            <th>Blocks</th>
                            <th>GPUs</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {instances.map((instance) => (
                            <tr key={instance.id}>
                                <td>{instance.hostname ?? instance.name}</td>
                                <td>{instance.fleet_name}</td>
                                <td>
                                    <Status value={instance.status} />
                                    {instance.unreachable ? ' (unreachable)' : ''}
                                </td>
                                <td>{instance.health_status}</td>
                                <td>
                                    {instance.busy_blocks}/{instance.total_blocks}
                                </td>
                                <td>
                                    {(grouped.get(instance.id) ?? [])
                                        .map((gpu) => `${gpu.name}${gpu.allocated ? '*' : ''}`)
                                        .join(', ') || '-'}
                                </td>
                                <td>
                                    {admin && instance.unreachable && (
                                        <button
                                            className="danger"
                                            onClick={async () => {
                                                const message = prompt('Reason for force decommission');
                                                if (message && confirm('Remote containers may remain. Continue?')) {
                                                    await apiRequest(token, 'instances/force_decommission', {
                                                        id: instance.id,
                                                        confirmation_message: message,
                                                        risk_acknowledged: true,
                                                    });
                                                    await reload();
                                                }
                                            }}
                                        >
                                            Force decommission
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function APIKeysPage() {
    const token = useToken();
    const { data = [], error, reload } = useLoad<APIKey[]>(token, 'api-keys/list');
    const [name, setName] = useState('');
    const [created, setCreated] = useState<APIKeyCreated>();
    const create = async () => {
        const result = await apiRequest<APIKeyCreated>(token, 'api-keys/create', { name });
        setCreated(result);
        setName('');
        await reload();
    };
    return (
        <>
            <h1>API Keys</h1>
            <div className="toolbar">
                <input placeholder="Key name" value={name} onChange={(event) => setName(event.target.value)} />
                <button className="primary" disabled={!name.trim()} onClick={create}>
                    Create
                </button>
            </div>
            {created && (
                <div className="success">
                    <b>Copy this key now. It cannot be shown again.</b>
                    <p>
                        <code>{created.key}</code>
                    </p>
                    <button onClick={() => setCreated(undefined)}>I have saved it</button>
                </div>
            )}
            <ErrorText error={error} />
            <div className="card">
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Prefix</th>
                            <th>Created</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((key) => (
                            <tr key={key.id}>
                                <td>{key.name}</td>
                                <td>
                                    <code>{key.prefix}</code>
                                </td>
                                <td>{new Date(key.created_at).toLocaleString()}</td>
                                <td>
                                    <button
                                        className="danger"
                                        onClick={async () => {
                                            if (confirm(`Revoke ${key.name}?`)) {
                                                await apiRequest(token, 'api-keys/revoke', { id: key.id });
                                                await reload();
                                            }
                                        }}
                                    >
                                        Revoke
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function RegistriesPage() {
    const admin = useAdmin();
    const token = useToken();
    const { data = [], error, reload } = useLoad<Registry[]>(token, 'registries/list');
    const [host, setHost] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    return (
        <>
            <h1>Registries</h1>
            {admin && (
                <div className="card">
                    <div className="grid">
                        <input placeholder="registry host" value={host} onChange={(event) => setHost(event.target.value)} />
                        <input placeholder="username" value={username} onChange={(event) => setUsername(event.target.value)} />
                        <input
                            type="password"
                            placeholder="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                        />
                    </div>
                    <div className="toolbar">
                        <button
                            className="primary"
                            onClick={async () => {
                                await apiRequest(token, 'registries/create', { host, username, password });
                                setHost('');
                                setUsername('');
                                setPassword('');
                                await reload();
                            }}
                        >
                            Add registry
                        </button>
                    </div>
                </div>
            )}
            <ErrorText error={error} />
            <div className="card">
                <table>
                    <thead>
                        <tr>
                            <th>Host</th>
                            <th>Username</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((registry) => (
                            <tr key={registry.id}>
                                <td>{registry.host}</td>
                                <td>{registry.username}</td>
                                <td>
                                    {admin && (
                                        <button
                                            className="danger"
                                            onClick={async () => {
                                                if (confirm(`Delete ${registry.host}?`)) {
                                                    await apiRequest(token, 'registries/delete', { id: registry.id });
                                                    await reload();
                                                }
                                            }}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function EventsPage() {
    const token = useToken();
    const { data = [], error, reload } = useLoad<Event[]>(token, 'events/list', { limit: 100, ascending: false });
    return (
        <>
            <h1>Events</h1>
            <div className="toolbar">
                <button onClick={reload}>Refresh</button>
            </div>
            <ErrorText error={error} />
            <div className="card">
                <table>
                    <thead>
                        <tr>
                            <th>Time</th>
                            <th>Actor</th>
                            <th>Target</th>
                            <th>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((event) => (
                            <tr key={event.id}>
                                <td>{new Date(event.recorded_at).toLocaleString()}</td>
                                <td>{event.actor}</td>
                                <td>{event.targets.map((target) => `${target.type} ${target.name}`).join(', ')}</td>
                                <td>{event.message}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default function App() {
    const [token, setToken] = useState(() => {
        if (document.cookie.split('; ').some((item) => item.startsWith('vstack-memhub-csrf='))) {
            localStorage.removeItem(TOKEN_KEY);
            return '';
        }
        return localStorage.getItem(TOKEN_KEY) ?? '';
    });
    const [principal, setPrincipal] = useState<Principal>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const refresh = () =>
        apiRequest<Principal>(token, 'auth/me')
            .then(setPrincipal)
            .catch((reason) => setError(String(reason)));
    useEffect(() => {
        const expired = () => {
            setPrincipal(undefined);
            localStorage.removeItem(TOKEN_KEY);
            setToken('');
        };
        window.addEventListener('vstack-auth-expired', expired);
        return () => window.removeEventListener('vstack-auth-expired', expired);
    }, []);
    useEffect(() => {
        apiRequest<Principal>(token, 'auth/me')
            .then(setPrincipal)
            .catch(() => setPrincipal(undefined))
            .finally(() => setLoading(false));
    }, [token]);
    if (loading) return <p>Loading identity…</p>;
    if (!principal)
        return (
            <>
                <Login onLogin={setToken} />
                <ErrorText error={error} />
            </>
        );
    const logout = async () => {
        try {
            await apiRequest<void>(token, 'auth/logout');
            localStorage.removeItem(TOKEN_KEY);
            setToken('');
            setPrincipal(undefined);
        } catch (reason) {
            setError(String(reason));
        }
    };
    return (
        <AuthContext.Provider value={{ token, principal }}>
            <BrowserRouter>
                <ErrorText error={error} />
                <Layout key={principal.role} logout={() => void logout()} refresh={() => void refresh()} />
            </BrowserRouter>
        </AuthContext.Provider>
    );
}
